/**
 * Mesa ya impresa: tarjeta verde con comensales, hora y «Impreso Nx».
 * «Reimprimir» abre la franja de reimpresión; el icono abre la página de la mesa.
 */

import { Check, Clock, ExternalLink, Printer, Users } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { DeskOrder } from '@/services/deskService';
import { orderTime, tableName } from './orderDisplay';

interface PrintedTableTileProps {
  order: DeskOrder;
  isSelected: boolean;
  onSelect: () => void;
}

export function PrintedTableTile({ order, isSelected, onSelect }: PrintedTableTileProps) {
  const name = tableName(order);
  const time = orderTime(order);
  const guests = order.identification.guests_count ?? 0;
  const count = order.memory?.print_count ?? 0;

  return (
    <article
      className={cn(
        'grid min-w-0 content-start gap-2 rounded-xl border bg-green-50 p-4 shadow-sm',
        isSelected ? 'border-content-primary' : 'border-green-200',
      )}
    >
      <div className="flex min-w-0 items-center gap-2">
        <h4 className="truncate text-xl font-bold text-gray-900">{name}</h4>
        <Check className="ml-auto h-5 w-5 shrink-0 text-green-600" aria-label="Impresa" />
      </div>

      <div className="flex items-center justify-between text-sm text-gray-600">
        <span className="flex items-center gap-1.5">
          <Users className="h-4 w-4" aria-hidden />
          {guests > 0 ? guests : '?'}
        </span>
        <span className="flex items-center gap-1.5 tabular-nums">
          <Clock className="h-4 w-4" aria-hidden />
          {time}
        </span>
      </div>

      <div className="mt-1 flex items-center gap-2">
        <span className="rounded bg-green-200 px-2 py-1 text-xs font-medium text-green-800">
          {count > 1 ? `Impreso ${count}x` : 'Impreso'}
        </span>
        <button
          type="button"
          onClick={onSelect}
          aria-pressed={isSelected}
          aria-label={`Reimprimir ${name}, ${time}`}
          className="ml-auto flex h-9 items-center gap-1.5 rounded-md border border-green-300 bg-white px-2.5 text-xs font-medium text-gray-800 hover:bg-green-100"
        >
          <Printer className="h-3.5 w-3.5" />
          Reimprimir
        </button>
        <a
          href={`/orders/${order.provider_order_id}`}
          target="_blank"
          rel="noreferrer"
          title="Abrir la página de la mesa"
          aria-label={`Abrir la página de ${name}`}
          className="flex h-9 w-9 items-center justify-center rounded-md text-gray-600 hover:bg-green-100 hover:text-gray-900"
        >
          <ExternalLink className="h-4 w-4" />
        </a>
      </div>
    </article>
  );
}
